import { Archive, Clock, MoreHorizontal, Trash2 } from 'lucide-react'
import { type ReactNode, useEffect, useRef, useState } from 'react'
import { MAIL_SWIPE_TRASH_RATIO, type MailMoveAction, MailSwipe } from '../../lib/mail-swipe'
import { useMediaQuery } from '../../lib/use-media-query'

/** Width of one revealed action button (Später, Papierkorb). */
const REVEAL_BUTTON_PX = 96

export function SwipeableRow({
  children,
  onAction,
  onSnooze,
  disabled = false,
  disabledHint,
}: {
  children: ReactNode
  onAction: (action: MailMoveAction) => void
  /** Omitted where snoozing is impossible (protected folder, „Später“ itself):
   * then neither the reveal nor the menu offers it. */
  onSnooze?: () => void
  disabled?: boolean
  /** Why the actions are locked (e.g. offline, #785): tooltip on the menu button. */
  disabledHint?: string
}) {
  const [menuOpen, setMenuOpen] = useState(false)
  const [revealed, setRevealed] = useState(false)
  const root = useRef<HTMLDivElement>(null)
  const surface = useRef<HTMLDivElement>(null)
  const archiveLayer = useRef<HTMLDivElement>(null)
  const leftLayer = useRef<HTMLDivElement>(null)
  const snoozeAction = useRef<HTMLButtonElement>(null)
  const trashAction = useRef<HTMLButtonElement>(null)
  const gesture = useRef(new MailSwipe())
  const pointer = useRef<number | null>(null)
  const revealedRef = useRef(false)
  const suppressClickUntil = useRef(0)
  const reduced = useMediaQuery('(prefers-reduced-motion: reduce)')
  const revealWidth = (onSnooze ? 2 : 1) * REVEAL_BUTTON_PX

  const settle = (offset: number) => {
    if (!surface.current) return
    surface.current.style.transition = reduced ? 'none' : 'transform 180ms ease-out'
    surface.current.style.transform = `translateX(${offset}px)`
  }
  /** Direct styles avoid React redraws while dragging: which half is shown,
   * and whether the drag is already past "ganz durch" (all red, Später hidden). */
  const paint = (delta: number, width: number) => {
    const left = delta < 0
    if (archiveLayer.current) archiveLayer.current.style.visibility = left ? 'hidden' : 'visible'
    if (leftLayer.current) leftLayer.current.style.visibility = left ? 'visible' : 'hidden'
    const through = left && width > 0 && -delta >= width * MAIL_SWIPE_TRASH_RATIO
    if (snoozeAction.current) snoozeAction.current.style.display = through ? 'none' : ''
    if (trashAction.current) trashAction.current.style.flexGrow = through ? '1' : ''
  }
  const reset = () => {
    settle(0)
    gesture.current.cancel()
    pointer.current = null
    if (revealedRef.current) {
      revealedRef.current = false
      setRevealed(false)
    }
  }
  const reveal = () => {
    paint(-1, 0)
    settle(-revealWidth)
    gesture.current.cancel()
    pointer.current = null
    revealedRef.current = true
    setRevealed(true)
  }
  const resetRef = useRef(reset)
  resetRef.current = reset
  // Locked while open (e.g. the connection dropped, #785): close revealed actions.
  useEffect(() => {
    if (!disabled) return
    setMenuOpen(false)
    if (revealedRef.current) resetRef.current()
  }, [disabled])
  useEffect(() => {
    const down = (e: PointerEvent) => {
      // A second finger anywhere on the page cancels the active gesture.
      if (pointer.current !== null && e.pointerId !== pointer.current) {
        suppressClickUntil.current = performance.now() + 500
        resetRef.current()
        return
      }
      // A tap outside or a swipe on another row closes the revealed actions;
      // presses on this row are handled by the surface / the buttons themselves.
      if (revealedRef.current && !root.current?.contains(e.target as Node)) resetRef.current()
    }
    document.addEventListener('pointerdown', down, true)
    return () => document.removeEventListener('pointerdown', down, true)
  }, [])
  return (
    <div
      ref={root}
      className="relative overflow-hidden rounded-2xl border border-border-subtle"
      data-swipe-row
    >
      <div
        ref={archiveLayer}
        aria-hidden="true"
        className="absolute inset-0 flex items-center gap-1 bg-success px-3 text-black"
      >
        <Archive size={18} /> Erledigt
      </div>
      {/* Left swipe: Später + Papierkorb anchored right. Decoration while dragging,
          real buttons once revealed. Später grows with the drag distance. */}
      <div
        ref={leftLayer}
        aria-hidden={!revealed}
        className="absolute inset-0 flex text-xs font-medium text-white"
        style={{ visibility: 'hidden' }}
      >
        {onSnooze && (
          <button
            ref={snoozeAction}
            type="button"
            tabIndex={revealed ? 0 : -1}
            aria-label="Konversation später erinnern"
            onClick={() => {
              reset()
              onSnooze()
            }}
            className="flex flex-1 justify-end bg-info"
          >
            <span
              className="flex h-full flex-col items-center justify-center gap-1"
              style={{ width: REVEAL_BUTTON_PX }}
            >
              <Clock size={20} aria-hidden="true" />
              Später
            </span>
          </button>
        )}
        <button
          ref={trashAction}
          type="button"
          tabIndex={revealed ? 0 : -1}
          aria-label="Konversation in den Papierkorb"
          onClick={() => {
            reset()
            onAction('trash')
          }}
          className={`flex justify-end bg-danger ${onSnooze ? 'flex-none' : 'flex-1'}`}
          style={onSnooze ? { width: REVEAL_BUTTON_PX } : undefined}
        >
          <span
            className="flex h-full flex-col items-center justify-center gap-1"
            style={{ width: REVEAL_BUTTON_PX }}
          >
            <Trash2 size={20} aria-hidden="true" />
            Papierkorb
          </span>
        </button>
      </div>
      {/* The wrapper observes gestures; all semantic actions are real buttons. */}
      <div
        ref={surface}
        className="relative bg-bg-elevated"
        style={{ touchAction: 'pan-y pinch-zoom' }}
        onPointerDown={(e) => {
          if (revealedRef.current) {
            // First press on a revealed row only closes it (no mail opens).
            suppressClickUntil.current = performance.now() + 500
            reset()
            return
          }
          if (disabled || !e.isPrimary || e.button !== 0) return
          if ((e.target as HTMLElement).closest('[data-swipe-control]')) return
          pointer.current = e.pointerId
          gesture.current.start(e.clientX, e.clientY)
        }}
        onPointerMove={(e) => {
          if (disabled || pointer.current !== e.pointerId) return
          const delta = gesture.current.move(e.clientX, e.clientY)
          if (delta === null) return
          e.currentTarget.setPointerCapture(e.pointerId)
          suppressClickUntil.current = performance.now() + 500
          paint(delta, e.currentTarget.clientWidth)
          e.currentTarget.style.transition = 'none'
          e.currentTarget.style.transform = reduced ? 'none' : `translateX(${delta}px)`
        }}
        onPointerUp={(e) => {
          if (pointer.current !== e.pointerId) return
          gesture.current.move(e.clientX, e.clientY)
          const action = gesture.current.end(e.currentTarget.clientWidth)
          if (action === 'reveal') {
            if (disabled) reset()
            else reveal()
            return
          }
          reset()
          if (action && !disabled) onAction(action)
        }}
        onPointerCancel={reset}
        onLostPointerCapture={(e) => {
          // After pointerup the gesture is already settled (possibly revealed).
          if (e.target === e.currentTarget && pointer.current !== null) reset()
        }}
        onClickCapture={(e) => {
          if (e.detail > 0 && performance.now() < suppressClickUntil.current) {
            e.preventDefault()
            e.stopPropagation()
          }
        }}
      >
        {children}
        <div data-swipe-control className="absolute right-1 top-1">
          <button
            type="button"
            disabled={disabled}
            title={disabled ? disabledHint : undefined}
            aria-label="Konversationsaktionen"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(!menuOpen)}
            className="flex min-h-11 min-w-11 items-center justify-center rounded-lg bg-bg-elevated text-text-secondary disabled:opacity-40"
          >
            <MoreHorizontal size={18} />
          </button>
        </div>
        {menuOpen && (
          <div
            data-swipe-control
            className="flex flex-wrap justify-end border-t border-border-subtle px-2"
          >
            <button
              type="button"
              disabled={disabled}
              onClick={() => {
                setMenuOpen(false)
                onAction('archive')
              }}
              className="min-h-11 px-2 text-xs text-text-secondary"
              aria-label="Konversation erledigen"
            >
              <Archive size={16} className="inline mr-1" />
              Erledigt
            </button>
            {onSnooze && (
              <button
                type="button"
                disabled={disabled}
                onClick={() => {
                  setMenuOpen(false)
                  onSnooze()
                }}
                className="min-h-11 px-2 text-xs text-text-secondary"
                aria-label="Konversation später erinnern"
              >
                <Clock size={16} className="inline mr-1" />
                Später
              </button>
            )}
            <button
              type="button"
              disabled={disabled}
              onClick={() => {
                setMenuOpen(false)
                onAction('trash')
              }}
              className="min-h-11 px-2 text-xs text-text-secondary"
              aria-label="Konversation in den Papierkorb"
            >
              <Trash2 size={16} className="inline mr-1" />
              Papierkorb
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
